<script setup lang="ts">
import ProductList from '~/components/plp/ProductList.vue';
import ProductListSkeleton from '~/components/plp/Skeleton.vue';
import { useRoute } from 'vue-router';

import SearchHeader from '~/components/search/SearchHeader.vue';
import {
	fetchSearchProducts,
	fetchTrendingProducts,
	useProductsList,
} from '~/data/products';
import FilterBar from '~/components/filter-bar/index.vue';
import OctaneSearchHeader from '~/components/search/OctaneSearchHeader.vue';

const { $cathodeClient } = useNuxtApp();

const routeName = 'search';
const route = useRoute();
definePageMeta({ middleware: ['search-guard'] });

const { deviceType } = useDeviceType();
const isMobile = computed(() => deviceType.value === 'mobile');

const isRouteChange = computed(() => {
	if (route.query.path !== '/search' && !data.value) return true;

	return false;
});

const GlobalStore = useGlobalStore();
const { storeType } = storeToRefs(GlobalStore);

const searchTerm = computed(() => (route.query?.search as string) || '');

const enableQuery = computed(() => {
	if (storeType.value === 'OCTANE') {
		return true;
	} else {
		return !!searchTerm.value;
	}
});

const nodeId = computed(() => {
	return route.query?.node_id ?? '';
});

const filters = computed(() => {
	const filters = route.fullPath.split('?')[1] || '';
	if (!filters) return '';
	const params = new URLSearchParams(filters);
	params.delete('search');
	params.delete('node_id');

	return params.toString();
});

const {
	data,
	isLoading,
	hasNextPage,
	fetchNextPage,
	suspense,
	isFetchingNextPage,
} = useProductsList({
	queryKey: [searchTerm, filters, nodeId],
	queryFn: ({ pageParam: cursor }) => {
		if (storeType.value === 'OCTANE' && !searchTerm.value) {
			return fetchTrendingProducts(cursor as string);
		} else {
			return fetchSearchProducts(
				searchTerm.value,
				filters.value,
				cursor as string,
				nodeId.value as string,
			);
		}
	},
	routeName,
	enabledOverride: enableQuery,
});

const isSearchData = computed(() => {
	return (
		!isLoading.value &&
		data.value &&
		data.value.pages.length &&
		data.value.pages[0]!.products.length
	);
});

onServerPrefetch(async () => {
	await suspense();
});

watch(data, () => {
	if (isLoading.value) {
		return;
	}

	const eventData = data.value?.pages[0]?.products.map((item) => {
		return {
			title: item.title,
			id: String(item.shopify_id),
			vendor: item.partner,
		};
	});

	$cathodeClient.queueEvent('search-query', {
		query_string: searchTerm.value,
		result: eventData || [],
		time: new Date().toUTCString(),
		source: window.location.href,
		type:
			!searchTerm.value && storeType.value === 'OCTANE' ? 'Trending' : 'Query',
	});
});
</script>
<template>
	<div class="relative">
		<OctaneSearchHeader v-if="storeType === 'OCTANE'" />

		<SearchHeader
			v-if="storeType !== 'OCTANE' && deviceType === 'mobile'"
			class="z-[20]"
		/>
		<PlpHeader
			v-if="!isMobile && isSearchData"
			:data="{
				title:
					storeType === 'OCTANE' && !searchTerm
						? 'Trending Products'
						: 'Search Results',
				subtitle: '',
				description: '',
			}"
			:show-count="false"
		/>

		<div
			v-else-if="isSearchData"
			class="flex px-3xl text-xl font-semibold sticky bg-white h-[calc(var(--plp-header-height-mobile))] top-0 left-0 right-0 z-[8]"
		>
			<span class="mt-xl">
				{{
					storeType === 'OCTANE' && !searchTerm
						? 'Trending Products'
						: 'Search Results'
				}}
			</span>
		</div>

		<ClientOnly v-if="isSearchData">
			<FilterBar v-if="searchTerm" :handle="searchTerm" />
		</ClientOnly>

		<div v-if="!isRouteChange">
			<ProductList
				v-if="!isLoading"
				class="z-[0]"
				:infinite-loader="searchTerm === ''"
				:fetch-next-page="fetchNextPage"
				:is-loading="isLoading"
				:data="data"
				:has-next-page="hasNextPage"
				:is-fetching-next-page="isFetchingNextPage"
			/>

			<ProductListSkeleton v-else />
		</div>
	</div>
</template>
